import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

const SVG_W = 860;
const SVG_H = 580;
const BG = '#0f1117';

// ── Force simulation (runs live via RAF) ─────────────────────────────────────

function initPositions(nodes, W, H) {
  return nodes.map(() => ({
    x: W / 2 + (Math.random() - 0.5) * W * 0.55,
    y: H / 2 + (Math.random() - 0.5) * H * 0.55,
    vx: 0,
    vy: 0,
  }));
}

function stepSimulation(pos, nodes, edges, idxById, W, H, alpha) {
  const n = nodes.length;
  const fx = new Array(n).fill(0);
  const fy = new Array(n).fill(0);

  // Repulsion
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const dx = pos[i].x - pos[j].x || 0.1;
      const dy = pos[i].y - pos[j].y || 0.1;
      const distSq = dx * dx + dy * dy || 0.01;
      const dist = Math.sqrt(distSq);
      const bothPaper = nodes[i].type === 'paper' && nodes[j].type === 'paper';
      const f = ((bothPaper ? 3200 : 1400) * alpha) / distSq;
      fx[i] += (dx / dist) * f;
      fy[i] += (dy / dist) * f;
      fx[j] -= (dx / dist) * f;
      fy[j] -= (dy / dist) * f;
    }
  }

  // Spring attraction along edges
  const IDEAL = 140;
  const K = 0.032;
  for (const edge of edges) {
    const si = idxById[edge.source];
    const ti = idxById[edge.target];
    if (si === undefined || ti === undefined) continue;
    const dx = pos[ti].x - pos[si].x;
    const dy = pos[ti].y - pos[si].y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const f = (dist - IDEAL) * K * alpha;
    fx[si] += (dx / dist) * f;
    fy[si] += (dy / dist) * f;
    fx[ti] -= (dx / dist) * f;
    fy[ti] -= (dy / dist) * f;
  }

  // Gravity toward center
  const G = 0.007 * alpha;
  for (let i = 0; i < n; i++) {
    fx[i] += (W / 2 - pos[i].x) * G;
    fy[i] += (H / 2 - pos[i].y) * G;
  }

  const DAMP = 0.87;
  return pos.map((p, i) => {
    const nvx = (p.vx + fx[i]) * DAMP;
    const nvy = (p.vy + fy[i]) * DAMP;
    return {
      x: Math.max(24, Math.min(W - 24, p.x + nvx)),
      y: Math.max(24, Math.min(H - 24, p.y + nvy)),
      vx: nvx,
      vy: nvy,
    };
  });
}

// ── Component ────────────────────────────────────────────────────────────────

export default function KnowledgeGraph({ graphData, loading, onRefresh }) {
  const svgRef = useRef(null);
  const posRef = useRef([]);
  const rafRef = useRef(null);
  const alphaRef = useRef(1);
  const nodeDragRef = useRef(null);
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });

  const [renderPos, setRenderPos] = useState([]);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [grabbing, setGrabbing] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [search, setSearch] = useState('');

  // ── Derived data ──────────────────────────────────────────────────────────

  const idxById = useMemo(() => {
    const m = {};
    graphData?.nodes.forEach((n, i) => { m[n.id] = i; });
    return m;
  }, [graphData]);

  // Collapse concept→paper→paper into direct paper-paper edges
  const paperPaperEdges = useMemo(() => {
    if (!graphData) return [];
    const conceptPapers = {};
    graphData.edges.forEach(({ source, target }) => {
      if (!conceptPapers[target]) conceptPapers[target] = [];
      conceptPapers[target].push(source);
    });
    const pairMap = {};
    Object.entries(conceptPapers).forEach(([conceptId, papers]) => {
      if (papers.length < 2) return;
      const cNode = graphData.nodes.find((n) => n.id === conceptId);
      const label = cNode?.label || conceptId.replace('concept_', '');
      for (let i = 0; i < papers.length; i++) {
        for (let j = i + 1; j < papers.length; j++) {
          const key = [papers[i], papers[j]].sort().join('||');
          if (!pairMap[key]) pairMap[key] = { source: papers[i], target: papers[j], concepts: [] };
          pairMap[key].concepts.push(label);
        }
      }
    });
    return Object.values(pairMap);
  }, [graphData]);

  const paperConnectionCount = useMemo(() => {
    const c = {};
    paperPaperEdges.forEach(({ source, target }) => {
      c[source] = (c[source] || 0) + 1;
      c[target] = (c[target] || 0) + 1;
    });
    return c;
  }, [paperPaperEdges]);

  const focusId = selectedNode || hoveredNode;

  const neighborIds = useMemo(() => {
    if (!focusId) return null;
    const s = new Set([focusId]);
    paperPaperEdges.forEach(({ source, target }) => {
      if (source === focusId) s.add(target);
      if (target === focusId) s.add(source);
    });
    graphData?.edges.forEach(({ source, target }) => {
      if (source === focusId) s.add(target);
      if (target === focusId) s.add(source);
    });
    return s;
  }, [focusId, paperPaperEdges, graphData]);

  const searchMatches = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    const s = new Set();
    graphData?.nodes.forEach((n) => {
      if (n.label?.toLowerCase().includes(q)) s.add(n.id);
    });
    return s;
  }, [search, graphData]);

  // ── Live physics ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!graphData?.nodes.length) {
      posRef.current = [];
      setRenderPos([]);
      return;
    }
    posRef.current = initPositions(graphData.nodes, SVG_W, SVG_H);
    alphaRef.current = 1;
    setSelectedNode(null);
    setHoveredNode(null);
    setSearch('');
    setPan({ x: 0, y: 0 });
    setZoom(1);

    let frame = 0;
    const tick = () => {
      if (alphaRef.current > 0.001 && nodeDragRef.current === null) {
        posRef.current = stepSimulation(
          posRef.current,
          graphData.nodes,
          graphData.edges,
          idxById,
          SVG_W,
          SVG_H,
          alphaRef.current,
        );
        alphaRef.current *= 0.991;
      }
      frame++;
      // Render every frame while hot, taper off as it cools
      const renderInterval = alphaRef.current > 0.1 ? 1 : alphaRef.current > 0.01 ? 2 : 6;
      if (frame % renderInterval === 0) {
        setRenderPos([...posRef.current]);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [graphData, idxById]);

  // ── Zoom ──────────────────────────────────────────────────────────────────

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const ratio = SVG_W / rect.width;
    const mx = (e.clientX - rect.left) * ratio;
    const my = (e.clientY - rect.top) * ratio;
    const factor = e.deltaY < 0 ? 1.12 : 0.89;
    setZoom((z) => {
      const nz = Math.max(0.15, Math.min(6, z * factor));
      setPan((p) => ({
        x: mx - (mx - p.x) * (nz / z),
        y: my - (my - p.y) * (nz / z),
      }));
      return nz;
    });
  }, []);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    svg.addEventListener('wheel', handleWheel, { passive: false });
    return () => svg.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ── Pan + node drag ───────────────────────────────────────────────────────

  const handleSVGMouseDown = (e) => {
    const tag = e.target.tagName;
    if (tag === 'svg' || tag === 'rect') {
      isPanningRef.current = true;
      panStartRef.current = { x: e.clientX, y: e.clientY };
      setGrabbing(true);
      e.preventDefault();
    }
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const ratio = SVG_W / rect.width;

      if (nodeDragRef.current !== null) {
        const { nodeIdx } = nodeDragRef.current;
        const dx = (e.clientX - nodeDragRef.current.lastX) * ratio / zoom;
        const dy = (e.clientY - nodeDragRef.current.lastY) * ratio / zoom;
        nodeDragRef.current.lastX = e.clientX;
        nodeDragRef.current.lastY = e.clientY;
        const p = posRef.current[nodeIdx];
        posRef.current[nodeIdx] = { x: p.x + dx, y: p.y + dy, vx: 0, vy: 0 };
        alphaRef.current = Math.max(alphaRef.current, 0.08);
        setRenderPos([...posRef.current]);
        return;
      }
      if (!isPanningRef.current) return;
      const dx = (e.clientX - panStartRef.current.x) * ratio;
      const dy = (e.clientY - panStartRef.current.y) * ratio;
      panStartRef.current = { x: e.clientX, y: e.clientY };
      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
    };
    const onUp = () => {
      nodeDragRef.current = null;
      isPanningRef.current = false;
      setGrabbing(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [zoom]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const nodeOpacity = (id) => {
    if (searchMatches) return searchMatches.has(id) ? 1 : 0.1;
    if (neighborIds) return neighborIds.has(id) ? 1 : 0.1;
    return 1;
  };

  const edgeVisible = (src, tgt) => {
    if (searchMatches) return false;
    if (neighborIds) return neighborIds.has(src) && neighborIds.has(tgt);
    return true;
  };

  const paperCount = graphData?.nodes.filter((n) => n.type === 'paper').length ?? 0;
  const conceptCount = graphData?.nodes.filter((n) => n.type === 'concept').length ?? 0;
  const pendingCount = graphData?.nodes.filter((n) => n.type === 'paper' && !n.has_concepts).length ?? 0;

  // ── Empty state ───────────────────────────────────────────────────────────

  if (!graphData && !loading) {
    return (
      <div
        className="rounded-xl p-10 text-center"
        style={{ background: BG, boxShadow: 'rgba(255,255,255,0.06) 0px 0px 0px 1px' }}
      >
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>
          Add papers to your workspace to build a knowledge graph.
        </p>
        <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.18)' }}>
          Concepts are extracted automatically as you add papers.
        </p>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const selectedNodeData = selectedNode
    ? graphData?.nodes.find((n) => n.id === selectedNode)
    : null;
  const selectedEdges = selectedNode
    ? paperPaperEdges.filter((e) => e.source === selectedNode || e.target === selectedNode)
    : [];

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: BG, boxShadow: 'rgba(255,255,255,0.06) 0px 0px 0px 1px' }}
    >
      {/* ── Header ── */}
      <div
        className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div>
          <h2 className="text-sm font-semibold text-white" style={{ letterSpacing: '-0.01em' }}>
            Knowledge Graph
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>
            {paperCount} paper{paperCount !== 1 ? 's' : ''} · {conceptCount} concept{conceptCount !== 1 ? 's' : ''} · {paperPaperEdges.length} connection{paperPaperEdges.length !== 1 ? 's' : ''}
            {pendingCount > 0 && <span style={{ color: '#fbbf24' }}> · {pendingCount} extracting</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter nodes…"
            className="text-xs px-2.5 py-1.5 rounded-md outline-none"
            style={{
              background: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.8)',
              border: '1px solid rgba(255,255,255,0.1)',
              width: 130,
            }}
          />
          {selectedNode && (
            <button
              onClick={() => setSelectedNode(null)}
              className="text-xs px-2.5 py-1.5 rounded-md"
              style={{
                background: 'rgba(96,165,250,0.12)',
                color: '#60a5fa',
                border: '1px solid rgba(96,165,250,0.25)',
              }}
            >
              Clear focus
            </button>
          )}
          {pendingCount > 0 && (
            <span className="flex items-center gap-1 text-xs" style={{ color: '#fbbf24' }}>
              <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Extracting
            </span>
          )}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={loading}
              className="text-xs px-2.5 py-1.5 rounded-md disabled:opacity-40"
              style={{
                background: 'rgba(255,255,255,0.06)',
                color: 'rgba(255,255,255,0.55)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* ── Legend ── */}
      <div
        className="px-4 py-2 flex flex-wrap gap-4 text-xs"
        style={{ color: 'rgba(255,255,255,0.28)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        <span className="flex items-center gap-1.5">
          <svg width="10" height="10"><circle cx="5" cy="5" r="4.5" fill="#e2e8f0" /></svg>
          Paper (larger = more connections)
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="10" height="10"><circle cx="5" cy="5" r="3" fill="#a78bfa" /></svg>
          Concept
        </span>
        <span>Scroll to zoom · Drag to pan · Click to focus</span>
      </div>

      {/* ── Canvas ── */}
      <div className="relative select-none">
        {loading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            style={{ background: 'rgba(15,17,23,0.75)', backdropFilter: 'blur(4px)' }}
          >
            <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Building graph…
            </div>
          </div>
        )}

        <svg
          ref={svgRef}
          width={SVG_W}
          height={SVG_H}
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          className="w-full"
          style={{ maxHeight: SVG_H, display: 'block', cursor: grabbing ? 'grabbing' : 'grab' }}
          onMouseDown={handleSVGMouseDown}
          onClick={() => { if (!nodeDragRef.current) setSelectedNode(null); }}
        >
          <defs>
            <pattern id="kg-dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.75" fill="rgba(255,255,255,0.055)" />
            </pattern>
            {/* Soft glow for regular nodes */}
            <filter id="kg-glow-sm" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            {/* Strong glow for selected node */}
            <filter id="kg-glow-lg" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="7" result="b1" />
              <feGaussianBlur stdDeviation="3" result="b2" />
              <feMerge>
                <feMergeNode in="b1" />
                <feMergeNode in="b2" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            {/* Concept glow */}
            <filter id="kg-glow-concept" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* Background + dot grid */}
          <rect width={SVG_W} height={SVG_H} fill={BG} />
          <rect width={SVG_W} height={SVG_H} fill="url(#kg-dots)" />

          <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>

            {/* ── Concept-paper edges (show only on focus) ── */}
            {renderPos.length > 0 && graphData?.edges.map((edge, i) => {
              const si = idxById[edge.source];
              const ti = idxById[edge.target];
              if (si === undefined || ti === undefined) return null;
              const p1 = renderPos[si], p2 = renderPos[ti];
              if (!p1 || !p2) return null;
              if (!neighborIds || !neighborIds.has(edge.source) || !neighborIds.has(edge.target)) return null;
              return (
                <line
                  key={`ce-${i}`}
                  x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                  stroke="#a78bfa"
                  strokeWidth="0.8"
                  opacity="0.3"
                  style={{ pointerEvents: 'none' }}
                />
              );
            })}

            {/* ── Paper-paper edges ── */}
            {renderPos.length > 0 && paperPaperEdges.map((edge, i) => {
              const si = idxById[edge.source];
              const ti = idxById[edge.target];
              if (si === undefined || ti === undefined) return null;
              const p1 = renderPos[si], p2 = renderPos[ti];
              if (!p1 || !p2) return null;
              const visible = edgeVisible(edge.source, edge.target);
              const isFocused = neighborIds
                ? (neighborIds.has(edge.source) && neighborIds.has(edge.target))
                : false;
              const opacity = isFocused ? 0.8 : visible ? 0.2 : 0;
              if (opacity === 0) return null;
              const stroke = isFocused ? '#60a5fa' : 'rgba(148,163,184,0.7)';
              const strokeW = isFocused ? 1.5 : 0.9;
              // Slight bezier curve
              const cx = (p1.x + p2.x) / 2 + (p2.y - p1.y) * 0.08;
              const cy = (p1.y + p2.y) / 2 - (p2.x - p1.x) * 0.08;
              return (
                <path
                  key={`pe-${i}`}
                  d={`M ${p1.x} ${p1.y} Q ${cx} ${cy} ${p2.x} ${p2.y}`}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={strokeW}
                  opacity={opacity}
                  style={{ pointerEvents: 'none' }}
                />
              );
            })}

            {/* ── Nodes ── */}
            {renderPos.length > 0 && graphData?.nodes.map((node, i) => {
              const pos = renderPos[i];
              if (!pos) return null;
              const isPaper = node.type === 'paper';
              const connections = isPaper ? (paperConnectionCount[node.id] || 0) : 0;
              const r = isPaper ? 9 + Math.min(connections * 4, 18) : 5;
              const isSelected = node.id === selectedNode;
              const isHovered = node.id === hoveredNode;
              const isFocused = isSelected || isHovered;
              const opacity = nodeOpacity(node.id);
              const showLabel = isFocused
                || (neighborIds && neighborIds.has(node.id))
                || (searchMatches && searchMatches.has(node.id));

              // Color
              let fill, glowFilter;
              if (isPaper) {
                if (isSelected) { fill = '#60a5fa'; glowFilter = 'url(#kg-glow-lg)'; }
                else if (isHovered) { fill = '#93c5fd'; glowFilter = 'url(#kg-glow-sm)'; }
                else { fill = node.has_concepts ? '#e2e8f0' : '#374151'; glowFilter = 'url(#kg-glow-sm)'; }
              } else {
                fill = isFocused ? '#c4b5fd' : '#7c3aed';
                glowFilter = 'url(#kg-glow-concept)';
              }

              return (
                <g
                  key={node.id}
                  transform={`translate(${pos.x},${pos.y})`}
                  opacity={opacity}
                  style={{ cursor: 'pointer' }}
                  onMouseDown={isPaper ? (e) => {
                    e.stopPropagation();
                    nodeDragRef.current = { nodeIdx: i, lastX: e.clientX, lastY: e.clientY };
                    e.preventDefault();
                  } : undefined}
                  onMouseEnter={(e) => { e.stopPropagation(); setHoveredNode(node.id); }}
                  onMouseLeave={() => setHoveredNode(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedNode((s) => s === node.id ? null : node.id);
                  }}
                >
                  {/* Selection ring */}
                  {isSelected && (
                    <circle
                      r={r + 6}
                      fill="none"
                      stroke={isPaper ? '#60a5fa' : '#a78bfa'}
                      strokeWidth="1.5"
                      opacity="0.45"
                      style={{ pointerEvents: 'none' }}
                    />
                  )}

                  {/* Main circle */}
                  <circle r={r} fill={fill} filter={glowFilter} />

                  {/* Short title inside paper nodes */}
                  {isPaper && r >= 12 && (() => {
                    const STOP = new Set(['a','an','the','of','in','on','and','for','to','with','by','at','from','is','are','using','via','based','study','analysis','novel','new']);
                    const words = (node.label || '')
                      .split(/\s+/)
                      .filter((w) => !STOP.has(w.toLowerCase()) && w.length > 1);
                    const fontSize = r >= 20 ? 7 : 6.5;
                    const maxChars = r >= 20 ? 11 : 9;
                    const textFill = isSelected ? '#fff' : (isHovered ? '#0f1117' : (node.has_concepts ? '#0f1117' : '#9ca3af'));

                    // Build up to 2 lines that each fit within maxChars
                    const lines = [];
                    let cur = '';
                    for (const w of words) {
                      const candidate = cur ? `${cur} ${w}` : w;
                      if (candidate.length <= maxChars) {
                        cur = candidate;
                      } else {
                        if (cur) lines.push(cur);
                        cur = w.slice(0, maxChars);
                        if (lines.length >= (r >= 20 ? 2 : 1)) break;
                      }
                    }
                    if (cur && lines.length < (r >= 20 ? 2 : 1)) lines.push(cur);
                    if (!lines.length && words[0]) lines.push(words[0].slice(0, maxChars));

                    const totalH = lines.length * (fontSize + 1.5);
                    const startY = -totalH / 2 + fontSize / 2;

                    return lines.map((line, idx) => (
                      <text
                        key={idx}
                        textAnchor="middle"
                        y={startY + idx * (fontSize + 1.5)}
                        fontSize={fontSize}
                        fontWeight="700"
                        fill={textFill}
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {line}
                      </text>
                    ));
                  })()}

                  {/* Label — only shown when focused/neighbour/search */}
                  {showLabel && (() => {
                    const label = node.label || '';
                    const max = isPaper ? 22 : 18;
                    const text = label.length > max ? label.slice(0, max - 1) + '…' : label;
                    return (
                      <text
                        y={r + 11}
                        textAnchor="middle"
                        fontSize={isPaper ? '7' : '6'}
                        fontWeight={isFocused ? '600' : '400'}
                        fill={
                          isFocused
                            ? (isPaper ? '#e2e8f0' : '#c4b5fd')
                            : 'rgba(255,255,255,0.4)'
                        }
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {text}
                      </text>
                    );
                  })()}
                </g>
              );
            })}

            {/* Placeholder while layout is computing */}
            {!renderPos.length && graphData?.nodes.length > 0 && (
              <text x={SVG_W / 2} y={SVG_H / 2} textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="13">
                Laying out…
              </text>
            )}
          </g>
        </svg>

        {/* ── Selected node detail card ── */}
        {selectedNodeData && selectedNodeData.type === 'paper' && (
          <div
            className="absolute bottom-3 left-3 text-xs rounded-xl"
            style={{
              background: 'rgba(15,17,23,0.94)',
              border: '1px solid rgba(96,165,250,0.28)',
              padding: '10px 14px',
              maxWidth: 270,
              backdropFilter: 'blur(12px)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
            }}
          >
            {selectedNodeData.doi_url ? (
              <a
                href={selectedNodeData.doi_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold leading-snug hover:underline"
                style={{ fontSize: '11px', letterSpacing: '-0.01em', color: '#93c5fd', display: 'block' }}
              >
                {selectedNodeData.label}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: 'inline', width: '9px', height: '9px', marginLeft: '4px', marginBottom: '1px', opacity: 0.7 }}
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            ) : (
              <p
                className="font-semibold leading-snug text-white"
                style={{ fontSize: '11px', letterSpacing: '-0.01em' }}
              >
                {selectedNodeData.label}
              </p>
            )}
            {(selectedNodeData.authors?.[0] || selectedNodeData.year) && (
              <p className="mt-0.5" style={{ color: 'rgba(255,255,255,0.38)', fontSize: '10px' }}>
                {[selectedNodeData.authors?.[0], selectedNodeData.year].filter(Boolean).join(' · ')}
              </p>
            )}
            <p className="mt-2" style={{ color: '#60a5fa', fontSize: '10px' }}>
              {paperConnectionCount[selectedNode] || 0} shared-concept connection{(paperConnectionCount[selectedNode] || 0) !== 1 ? 's' : ''}
            </p>
            {selectedEdges.length > 0 && (
              <div className="mt-1.5 space-y-0.5">
                {selectedEdges.slice(0, 4).map((e, i) => {
                  const otherId = e.source === selectedNode ? e.target : e.source;
                  const other = graphData.nodes.find((n) => n.id === otherId);
                  return (
                    <p key={i} style={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px' }}>
                      <span style={{ color: '#a78bfa' }}>↔ </span>
                      {other?.label?.slice(0, 26) || otherId}{other?.label?.length > 26 ? '…' : ''}
                    </p>
                  );
                })}
                {selectedEdges.length > 4 && (
                  <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '10px' }}>
                    +{selectedEdges.length - 4} more
                  </p>
                )}
              </div>
            )}
            {!selectedNodeData.has_concepts && (
              <p className="mt-1.5 font-medium" style={{ color: '#fbbf24', fontSize: '10px' }}>
                Extracting concepts…
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
